import {
    HsabAccountNametag,
    HsabExpenseCategory,
    HsabExpenseCategoryDisplay,
    HsabExpenseTypeResponse,
    HsabPlanSubType,
    HsabPlanType,
    WexExpenseAccountType
} from "@/shared/types";
import _ from "lodash";

interface EligiblePlanCombo {
    type: HsabPlanType;
    anySubType: boolean;
    subTypes?: HsabPlanSubType[];
}

export interface PlanTypesAndSubTypes {
    types: HsabPlanType[];
    subTypes: HsabPlanSubType[];
    allowHealthcareClaims: boolean;
    hasSmartCommutePlan: boolean;
    hasMedicalPlan: boolean;
}

export function getAllDisplayCategories (planTypesAndSubtypes: {
    types: HsabPlanType[],
    subTypes: HsabPlanSubType[],
    hasSmartCommutePlan: boolean,
    hasMedicalPlan: boolean,
    allowHealthcareClaims: boolean }) {
    const results: HsabExpenseCategoryDisplay[] = [];

    if (includeMedical(planTypesAndSubtypes)) {
        results.push(MedicalCategoryDisplay);
    }
    if (includeDental(planTypesAndSubtypes)) {
        results.push(DentalCategoryDisplay);
    }
    if (includeVision(planTypesAndSubtypes)) {
        results.push(VisionCategoryDisplay);
    }
    if (includePharmacy(planTypesAndSubtypes)) {
        results.push(PharmacyCategoryDisplay);
    }
    if (includePremium(planTypesAndSubtypes)) {
        results.push(PremiumCategoryDisplay);
    }
    if (includeDependentCare(planTypesAndSubtypes)) {
        results.push(DependentCareCategoryDisplay);
    }
    if (includeParking(planTypesAndSubtypes)) {
        results.push(ParkingCategoryDisplay);
    }
    if (includeMassTransit(planTypesAndSubtypes)) {
        results.push(MassTransitCategoryDisplay);
    }
    if (includeLifestyle(planTypesAndSubtypes)) {
        results.push(LifestyleCategoryDisplay);
    }
    if (includeTuition(planTypesAndSubtypes)) {
        results.push(TuitionCategoryDisplay);
    }

    return results;
}

function shouldDisplay (planTypesAndSubtypes: PlanTypesAndSubTypes, eligiblePlanCombos: EligiblePlanCombo[]): boolean {
    let result = false;

    _.forEach(eligiblePlanCombos, combo => {
        const hasType = planTypesAndSubtypes.types.some(x => x === combo.type);
        if (hasType && combo.anySubType) {
            result = true;
            return false; // break
        } else {
            const planTypeIndex = planTypesAndSubtypes.types.findIndex(x => x === combo.type);
            if (planTypeIndex !== -1) {
                const subType = planTypesAndSubtypes.subTypes[planTypeIndex];
                if (subType) {
                    const hasValidSubType = combo.subTypes.some(x => x === subType);
                    if (hasValidSubType) {
                        result = true;
                        return false; // break
                    }
                }
            }
        }
    });

    return result;
}

export function includeMedical (planTypesAndSubtypes: PlanTypesAndSubTypes): boolean {
    const eligiblePlanCombos: EligiblePlanCombo[] = [
        {
            type: HsabPlanType.HealthSavingAccount,
            anySubType: true
        },
        {
            type: HsabPlanType.HealthReimbursementAccount,
            anySubType: false,
            subTypes: [
                HsabPlanSubType.None,
                HsabPlanSubType.RetirementHra,
                HsabPlanSubType.WellnessHra
            ]
        },
        {
            type: HsabPlanType.MedicalFlex,
            anySubType: false,
            subTypes: [
                HsabPlanSubType.None,
            ]
        },
    ];
    return shouldDisplay(planTypesAndSubtypes, eligiblePlanCombos);
}

export function includeDental (planTypesAndSubtypes: {
    types: HsabPlanType[],
    subTypes: HsabPlanSubType[],
    hasSmartCommutePlan: boolean,
    hasMedicalPlan: boolean,
    allowHealthcareClaims: boolean}): boolean {
    const eligiblePlanCombos: EligiblePlanCombo[] = [
        {
            type: HsabPlanType.HealthSavingAccount,
            anySubType: true
        },
        {
            type: HsabPlanType.HealthReimbursementAccount,
            anySubType: false,
            subTypes: [
                HsabPlanSubType.None,
                HsabPlanSubType.RetirementHra,
                HsabPlanSubType.WellnessHra
            ]
        },
        {
            type: HsabPlanType.MedicalFlex,
            anySubType: false,
            subTypes: [
                HsabPlanSubType.None,
                HsabPlanSubType.LimitedPurposeFsa,
            ]
        },
    ];
    return shouldDisplay(planTypesAndSubtypes, eligiblePlanCombos);
}

export function includeVision (planTypesAndSubtypes: {
    types: HsabPlanType[],
    subTypes: HsabPlanSubType[],
    hasSmartCommutePlan: boolean,
    hasMedicalPlan: boolean,
    allowHealthcareClaims: boolean }): boolean {
    const eligiblePlanCombos: EligiblePlanCombo[] = [
        {
            type: HsabPlanType.HealthSavingAccount,
            anySubType: true
        },
        {
            type: HsabPlanType.HealthReimbursementAccount,
            anySubType: false,
            subTypes: [
                HsabPlanSubType.None,
                HsabPlanSubType.RetirementHra,
                HsabPlanSubType.WellnessHra
            ]
        },
        {
            type: HsabPlanType.MedicalFlex,
            anySubType: false,
            subTypes: [
                HsabPlanSubType.None,
                HsabPlanSubType.LimitedPurposeFsa,
            ]
        },
    ];
    return shouldDisplay(planTypesAndSubtypes, eligiblePlanCombos);
}

export function includePharmacy (planTypesAndSubtypes: {
    types: HsabPlanType[],
    subTypes: HsabPlanSubType[],
    hasSmartCommutePlan: boolean,
    hasMedicalPlan: boolean,
    allowHealthcareClaims: boolean
}): boolean {
    const eligiblePlanCombos: EligiblePlanCombo[] = [
        {
            type: HsabPlanType.HealthSavingAccount,
            anySubType: true
        },
        {
            type: HsabPlanType.HealthReimbursementAccount,
            anySubType: false,
            subTypes: [
                HsabPlanSubType.None,
                HsabPlanSubType.RetirementHra,
                HsabPlanSubType.WellnessHra
            ]
        },
        {
            type: HsabPlanType.MedicalFlex,
            anySubType: false,
            subTypes: [
                HsabPlanSubType.None,
            ]
        },
    ];
    return shouldDisplay(planTypesAndSubtypes, eligiblePlanCombos);
}

export function includePremium (planTypesAndSubtypes: { types: HsabPlanType[], subTypes: HsabPlanSubType[] }): boolean {
    return false;
}

export function includeDependentCare (planTypesAndSubtypes: {
    types: HsabPlanType[],
    subTypes: HsabPlanSubType[],
    hasSmartCommutePlan: boolean,
    hasMedicalPlan: boolean,
    allowHealthcareClaims: boolean,
}): boolean {
    const eligiblePlanCombos: EligiblePlanCombo[] = [
        {
            type: HsabPlanType.DependentCare,
            anySubType: true
        },
    ];
    return shouldDisplay(planTypesAndSubtypes, eligiblePlanCombos);
}

export function includeParking (planTypesAndSubtypes: {
    types: HsabPlanType[],
    subTypes: HsabPlanSubType[],
    hasSmartCommutePlan: boolean,
    hasMedicalPlan: boolean,
    allowHealthcareClaims: boolean }): boolean {
    const eligiblePlanCombos: EligiblePlanCombo[] = [
        {
            type: HsabPlanType.CommuterParking,
            anySubType: true
        },
    ];
    return shouldDisplay(planTypesAndSubtypes, eligiblePlanCombos);
}

export function includeMassTransit (planTypesAndSubtypes: {
    types: HsabPlanType[],
    subTypes: HsabPlanSubType[]
}): boolean {
    // Disabled for BEND-20634
    // const eligiblePlanCombos: EligiblePlanCombo[] = [
    //     {
    //         type: HsabPlanType.CommuterTransit,
    //         anySubType: true
    //     },
    // ];
    // return shouldDisplay(planTypesAndSubtypes, eligiblePlanCombos);
    return false;
}

export function includeLifestyle (planTypesAndSubtypes: {
    types: HsabPlanType[],
    subTypes: HsabPlanSubType[],
    hasSmartCommutePlan: boolean,
    hasMedicalPlan: boolean,
    allowHealthcareClaims: boolean
}): boolean {
    const eligiblePlanCombos: EligiblePlanCombo[] = [
        {
            type: HsabPlanType.HealthReimbursementAccount,
            anySubType: false,
            subTypes: [
                HsabPlanSubType.LifestyleSpendingAccount
            ]
        },
    ];
    return shouldDisplay(planTypesAndSubtypes, eligiblePlanCombos);
}

export function includeTuition (planTypesAndSubtypes: { types: HsabPlanType[], subTypes: HsabPlanSubType[] }): boolean {
    return false;
}

export const MedicalCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Medical,
    external: WexExpenseAccountType.Medical,
    display: "Medical",
    showTypeSelector: true,
    defaultTypeWhenSelected: "Medical Expenses",
    defaultSubTypeWhenSelected: plans => {
        if (hasLimitedFsaPlan(plans)) {
            return "Medical Services";
        }
        return null;
    },
};

export const DentalCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Dental,
    external: WexExpenseAccountType.Medical,
    display: "Dental",
    showTypeSelector: false,
    defaultTypeWhenSelected: "Dental",
};

export const VisionCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Vision,
    external: WexExpenseAccountType.Medical,
    display: "Vision",
    showTypeSelector: false,
    defaultTypeWhenSelected: "Vision",
};

export const PharmacyCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Pharmacy,
    external: WexExpenseAccountType.Medical,
    display: "Pharmacy",
    showTypeSelector: false,
    defaultTypeWhenSelected: "Drugs & Medicine",
    defaultSubTypeWhenSelected: plans => {
        if (hasLimitedFsaPlan(plans)) {
            return "Other Drugs & Medicine";
        }
        return null;
    },
};

export const DependentCareCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.DependentCare,
    external: WexExpenseAccountType.DependentCare,
    display: "Dependent Care",
    showTypeSelector: true,
    defaultTypeWhenSelected: null,
};

export const ParkingCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Parking,
    external: WexExpenseAccountType.Transportation,
    display: "Parking",
    showTypeSelector: false,
    defaultTypeWhenSelected: "Transportation - Parking",
};

export const MassTransitCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.MassTransit,
    external: WexExpenseAccountType.Transportation,
    display: "Mass Transit",
    showTypeSelector: false,
    defaultTypeWhenSelected: "Transportation",
};

export const PremiumCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Premium,
    external: WexExpenseAccountType.Premium,
    display: "Premium",
    showTypeSelector: true,
    defaultTypeWhenSelected: null,
};

export const LifestyleCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Lifestyle,
    external: WexExpenseAccountType.Lifestyle,
    display: "Lifestyle",
    showTypeSelector: true,
    defaultTypeWhenSelected: null,
};

export const TuitionCategoryDisplay: HsabExpenseCategoryDisplay = {
    category: HsabExpenseCategory.Tuition,
    external: WexExpenseAccountType.Tuition,
    display: "Tuition",
    showTypeSelector: true,
    defaultTypeWhenSelected: null,
};

export function hasLimitedFsaPlan (plans: HsabAccountNametag[]) {
    return plans.some(x => x.isActive && x.planType === HsabPlanType.MedicalFlex && x.planSubType === HsabPlanSubType.LimitedPurposeFsa);
}

export function removeItemsIfUserHasLimitedFsaPlan (plans: HsabAccountNametag[], items: HsabExpenseTypeResponse[]): HsabExpenseTypeResponse[] {
    if (hasLimitedFsaPlan(plans)) {
        for (const type of items) {
            type.expenseSubTypes = type.expenseSubTypes.filter(x => x.planEligibility.some(y => y.planDisplayName !== "HSA"));
        }

        return items.filter(x => x.expenseSubTypes.some(y => y.planEligibility.some(z => z.planDisplayName !== "HSA")));
    }
    return items;
}